import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import ApiService from '../../../../services/ApiService';
import { AMBIENTE_LABEL, extractApiError } from './helpers';
import {
  Banner,
  Dialog,
  DialogActions,
  DialogTitle,
  FieldError,
  FieldLabel,
  MutedText,
  Overlay,
  PillButton,
  TextInput,
} from './index.style';
import { FieldsRow } from './NumberingModal.style';
import { AmbienteEmissao, EmissorPainel } from './types';

export const numeracaoSchema = z.object({
  serieNFe: z.string().trim().min(1, 'Informe a série.'),
  sequencialNFe: z.coerce.number().int('Use um número inteiro.').min(1, 'Mínimo 1.'),
  sequencialLoteNFe: z.coerce.number().int('Use um número inteiro.').min(1, 'Mínimo 1.'),
});

export type NumeracaoForm = z.infer<typeof numeracaoSchema>;

interface NumberingModalProps {
  emissor: EmissorPainel;
  ambiente: AmbienteEmissao;
  onClose: () => void;
  onSaved: (emissor: EmissorPainel) => void;
}

const NumberingModal = ({ emissor, ambiente, onClose, onSaved }: NumberingModalProps) => {
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<NumeracaoForm>({
    resolver: zodResolver(numeracaoSchema),
    defaultValues: { serieNFe: '1', sequencialNFe: 1, sequencialLoteNFe: 1 },
  });

  const onSubmit = async (values: NumeracaoForm) => {
    setErroEnvio(null);
    try {
      const { data } = await ApiService.fiscal.configurarNumeracaoEmissor(emissor.id, { ambiente, ...values });
      onSaved(data);
    } catch (error) {
      setErroEnvio(extractApiError(error, 'Não foi possível configurar a numeração.').message);
    }
  };

  return ReactDOM.createPortal(
    <Overlay onClick={onClose}>
      <Dialog onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle>Configurar numeração de {AMBIENTE_LABEL[ambiente].toLowerCase()}</DialogTitle>
        <MutedText>
          Define de onde a sequência começa. Depois de configurada, a numeração é só leitura: quem conta os números é o
          Nota Gateway.
        </MutedText>
        {ambiente === 'PRODUCAO' && (
          <Banner $tone='warn'>
            Estes números vão ser gravados no Nota Gateway e não podem ser trocados depois. Confira com a contabilidade a
            série e o último número já usado neste CNPJ.
          </Banner>
        )}
        <FieldsRow>
          <FieldLabel>
            Série
            <TextInput {...register('serieNFe')} />
            {errors.serieNFe && <FieldError>{errors.serieNFe.message}</FieldError>}
          </FieldLabel>
          <FieldLabel>
            Nº inicial
            <TextInput type='number' min={1} {...register('sequencialNFe')} />
            {errors.sequencialNFe && <FieldError>{errors.sequencialNFe.message}</FieldError>}
          </FieldLabel>
          <FieldLabel>
            Lote inicial
            <TextInput type='number' min={1} {...register('sequencialLoteNFe')} />
            {errors.sequencialLoteNFe && <FieldError>{errors.sequencialLoteNFe.message}</FieldError>}
          </FieldLabel>
        </FieldsRow>
        {erroEnvio && <Banner $tone='bad'>{erroEnvio}</Banner>}
        <DialogActions>
          <PillButton type='button' onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </PillButton>
          <PillButton type='submit' $variant='primary' disabled={isSubmitting}>
            {isSubmitting ? 'Configurando…' : 'Configurar'}
          </PillButton>
        </DialogActions>
      </Dialog>
    </Overlay>,
    document.body,
  );
};

export default NumberingModal;
